// (c) chaehongee. Confidential and proprietary.

import { useEffect, useRef, useState } from 'react'
import type { ReactElement, RefObject, ChangeEvent, KeyboardEvent } from 'react'
import styles from '../Backstage.module.css'
import IconButton from '../../IconButton/IconButton'
import { Tooltip } from '../../shared/Tooltip/Tooltip'
import { ImagePlus, ArrowUp } from 'lucide-react'
import { SelectedImagePreviews } from './SelectedImagePreviews'
import type { ContextUsage } from '../../../store/slices/chatSlice'
import { useAppDispatch, useAppSelector } from '../../../store/hooks/hooks'
import {
    selectGenerationParams,
    setAspectRatio,
    setResolution,
    setModel,
} from '../../../store/slices/generationSlice'
import { AspectRatioDropdown } from './AspectRatioDropdown'
import { ModelDropdown } from './ModelDropdown'
import { ResolutionDropdown } from './ResolutionDropdown'

interface ChatInputAreaProps {
    input: string
    sessionActive: boolean
    readOnly: boolean
    canSend: boolean
    contextUsage: ContextUsage | null
    fileInputRef: RefObject<HTMLInputElement | null>
    textareaRef: RefObject<HTMLTextAreaElement | null>
    onPickFile: () => void
    onFileChange: (e: ChangeEvent<HTMLInputElement>) => void
    onInputChange: (value: string) => void
    onKeyDown: (e: KeyboardEvent<HTMLTextAreaElement>) => void
    onCompositionStart: () => void
    onCompositionEnd: () => void
    onSend: () => void
}

export function ChatInputArea(props: ChatInputAreaProps): ReactElement {
    const {
        input,
        sessionActive,
        readOnly,
        canSend,
        contextUsage,
        fileInputRef,
        textareaRef,
        onPickFile,
        onFileChange,
        onInputChange,
        onKeyDown,
        onCompositionStart,
        onCompositionEnd,
        onSend,
    } = props

    const dispatch = useAppDispatch()
    const generation = useAppSelector(selectGenerationParams)

    // Send button highlight from preset flow
    const [highlightSend, setHighlightSend] = useState(false)
    const highlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    useEffect(() => {
        const handler = () => {
            setHighlightSend(true)
            if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current)
            highlightTimerRef.current = setTimeout(() => setHighlightSend(false), 3000)
        }
        window.addEventListener('preset:prompt-ready', handler)
        return () => {
            window.removeEventListener('preset:prompt-ready', handler)
            if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current)
        }
    }, [])

    const isInputDisabled = readOnly || !sessionActive
    const usage = contextUsage

    const usagePct =
        usage && usage.contextWindowLimit > 0
            ? clamp01(usage.inputTokens / usage.contextWindowLimit)
            : null

    const usageTone =
        usagePct == null
            ? 'none'
            : usagePct >= 1
                ? 'danger'
                : usagePct >= 0.8
                    ? 'warn'
                    : 'ok'

    const usageText =
        usage && usage.contextWindowLimit > 0
            ? `${usage.inputTokens.toLocaleString()} / ${usage.contextWindowLimit.toLocaleString()} tokens`
            : null

    const usagePctLabel =
        usagePct == null
            ? null
            : `${Math.round(usagePct * 100)}%`

    return (
        <div className={styles.inputBar}>
            <SelectedImagePreviews />
            {usageTone === 'danger' && (
                <div className={styles.contextUsageNudge}>
                    Context is getting full — consider starting a new session.
                </div>
            )}
            <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={onFileChange}
                accept="image/*"
                disabled={readOnly}
            />
            <div className={styles.inputControls}>
                <IconButton
                    className={styles.fileButton}
                    onClick={readOnly ? undefined : onPickFile}
                    aria-label="Add image"
                    disabled={readOnly}
                >
                    <ImagePlus className={styles.icon} aria-hidden="true" />
                </IconButton>
                <AspectRatioDropdown
                    value={generation.aspect_ratio}
                    disabled={isInputDisabled}
                    onChange={(value: string) => dispatch(setAspectRatio(value))}
                />
                <ResolutionDropdown
                    value={generation.resolution}
                    disabled={isInputDisabled}
                    onChange={(value: string) => dispatch(setResolution(value))}
                />
                <ModelDropdown
                    value={generation.model}
                    disabled={isInputDisabled}
                    onChange={(value: string) => dispatch(setModel(value))}
                />
            </div>
            <textarea
                ref={textareaRef}
                className={styles.input}
                placeholder={
                    readOnly
                        ? 'Sign in to send messages'
                        : sessionActive
                            ? 'Ask for changes'
                            : 'Create or enter a session to chat'
                }
                value={input}
                onChange={(e) => onInputChange(e.currentTarget.value)}
                onKeyDown={onKeyDown}
                onCompositionStart={onCompositionStart}
                onCompositionEnd={onCompositionEnd}
                rows={1}
                disabled={isInputDisabled}
                title={readOnly ? 'Sign in to chat' : undefined}
            />
            <div className={styles.sendCluster}>
                {usagePct != null && usageText && (
                    <Tooltip
                        side="top"
                        className={styles.contextUsageTooltip}
                        contentClassName={styles.contextUsageBubble}
                        content={
                            <>
                                <div className={styles.contextUsageTooltipTitle}>{usagePctLabel} · {usageText}</div>
                                {usage?.model && (
                                    <div className={styles.contextUsageTooltipMeta}>
                                        Model: {usage.model}
                                    </div>
                                )}
                                <div className={styles.contextUsageTooltipHint}>
                                    When full, start a new session for better results.
                                </div>
                            </>
                        }
                    >
                        <div
                            className={[
                                styles.contextUsageDonut,
                                usageTone === 'warn' ? styles.contextUsageDonutWarn : '',
                                usageTone === 'danger' ? styles.contextUsageDonutDanger : '',
                            ].join(' ').trim()}
                            aria-label={`Context usage ${usagePctLabel ?? ''} (${usageText})`}
                        >
                            <Donut percent={usagePct} label={usagePctLabel ?? ''} />
                            <span className={styles.srOnly}>{usageText}</span>
                        </div>
                    </Tooltip>
                )}
                <button
                    className={styles.sendButton}
                    onClick={() => { setHighlightSend(false); onSend() }}
                    disabled={!canSend || readOnly}
                    aria-label="Send"
                    title={readOnly ? 'Sign in to send messages' : undefined}
                    data-highlight={highlightSend ? 'true' : undefined}
                >
                    <ArrowUp className={styles.sendIcon} color="#fff" aria-hidden="true" />
                </button>
            </div>
        </div>
    )
}

function Donut(props: { percent: number; label: string }): ReactElement {
    const { percent, label } = props
    const size = 22
    const stroke = 3
    const r = (size - stroke) / 2
    const c = 2 * Math.PI * r
    const offset = c * (1 - clamp01(percent))

    return (
        <svg
            className={styles.donutSvg}
            width={size}
            height={size}
            viewBox={`0 0 ${size} ${size}`}
            role="img"
            aria-hidden="true"
        >
            <circle
                className={styles.donutTrack}
                cx={size / 2}
                cy={size / 2}
                r={r}
                strokeWidth={stroke}
                fill="none"
            />
            <circle
                className={styles.donutProgress}
                cx={size / 2}
                cy={size / 2}
                r={r}
                strokeWidth={stroke}
                fill="none"
                strokeDasharray={`${c}`}
                strokeDashoffset={`${offset}`}
            />
            <text
                className={styles.donutLabel}
                x="50%"
                y="50%"
                dominantBaseline="central"
                textAnchor="middle"
            >
                {label}
            </text>
        </svg>
    )
}

function clamp01(value: number): number {
    if (Number.isNaN(value)) return 0
    if (value < 0) return 0
    if (value > 1) return 1
    return value
}

